import doubleClickZoom from './lib/doubleClickZoom';
import * as Constants from './constants';
import createVertex from './lib/createVertex';

const SurveyPolygonMode: any = {};
let surveyPosition: GeolocationPosition | null;
let lastAddedPosition: GeolocationPosition | null | any;
let usablePosition: GeolocationPosition | null;
let recentPositions: GeolocationPosition[] = [];
const positionHistoryLimit = 5;

let polygon;

let currentVertexPosition = 0;

let surveyInterval;

let surveyCount = 0;
let lastUsedSurveyCount = 0;

function averagePosition(
    positions: GeolocationPosition[],
): GeolocationPosition | null {
    if (positions.length === 0) return null;

    const filteredPositions = filterOutliers(positions);

    if (filteredPositions.length === 0) return null;

    let latSum = 0;
    let lonSum = 0;

    filteredPositions.forEach((pos) => {
        latSum += pos.coords.latitude;
        lonSum += pos.coords.longitude;
    });

    const averagedCoords = {
        latitude: latSum / filteredPositions.length,
        longitude: lonSum / filteredPositions.length,
        accuracy: filteredPositions[0].coords.accuracy,
        altitude: filteredPositions[0].coords.altitude,
        altitudeAccuracy: filteredPositions[0].coords.altitudeAccuracy,
        heading: filteredPositions[0].coords.heading,
        speed: filteredPositions[0].coords.speed,
    };

    return {
        coords: averagedCoords,
        timestamp: Date.now(),
    };
}

function filterOutliers(
    positions: GeolocationPosition[],
): GeolocationPosition[] {
    if (positions.length === 0) return [];

    const average = {
        latitude:
            positions.reduce((sum, pos) => sum + pos.coords.latitude, 0) /
            positions.length,
        longitude:
            positions.reduce((sum, pos) => sum + pos.coords.longitude, 0) /
            positions.length,
    };

    const threshold = 50;

    return positions.filter((pos) => {
        const distance = calculateDistance(
            average.latitude,
            average.longitude,
            pos.coords.latitude,
            pos.coords.longitude,
        );
        return distance <= threshold;
    });
}

function calculateDistance(lat1, lon1, lat2, lon2) {
    const earthRadius = 6371e3;
    const lat1Rad = toRadians(lat1);
    const lat2Rad = toRadians(lat2);
    const deltaLatRad = toRadians(lat2 - lat1);
    const deltaLonRad = toRadians(lon2 - lon1);

    const a =
        Math.sin(deltaLatRad / 2) * Math.sin(deltaLatRad / 2) +
        Math.cos(lat1Rad) *
            Math.cos(lat2Rad) *
            Math.sin(deltaLonRad / 2) *
            Math.sin(deltaLonRad / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return earthRadius * c;
}

function toRadians(degrees: number): number {
    return (degrees * Math.PI) / 180;
}

SurveyPolygonMode.getPosition = () => {
    window.navigator.geolocation.getCurrentPosition(
        (position: GeolocationPosition) => {
            surveyPosition = position;
            recentPositions.push(position);
            if (recentPositions.length > positionHistoryLimit) {
                recentPositions.shift();
            }
            surveyCount++;
        },
    );
};

SurveyPolygonMode.addPointAndRender = function () {
    if (surveyPosition) {
        usablePosition = surveyPosition;

        polygon.addCoordinate(
            `0`,
            usablePosition.coords.longitude,
            usablePosition.coords.latitude,
        );
        lastAddedPosition = usablePosition;
        lastUsedSurveyCount = surveyCount;
    }

    polygon.ctx.store.render();
};

SurveyPolygonMode.removeLastPointAndRender = function () {
    let lastPoint = polygon.coordinates[0].shift();

    const lastPointCheck = () => {
        if (
            polygon.coordinates[0][0][0] === lastPoint[0] &&
            polygon.coordinates[0][0][1] === lastPoint[1]
        ) {
            polygon.coordinates[0].shift();
            lastPointCheck();
        }
    };

    lastPointCheck();

    polygon.updateCoordinate(
        `0.${currentVertexPosition}`,
        polygon.coordinates[0][0][0],
        polygon.coordinates[0][0][1],
    );

    polygon.ctx.store.render();

    lastAddedPosition.coords.longitude = polygon.coordinates[0][0][0];
    lastAddedPosition.coords.latitude = polygon.coordinates[0][0][1];
};

SurveyPolygonMode.retryAddPointAndRender = function () {
    const retry = () => {
        setTimeout(() => {
            this.retryAddPointAndRender();
        }, 500);
    };

    const done = () => {
        this.addPointAndRender();
    };

    if (!surveyPosition) {
        this.getPosition();
        retry();
        return;
    }
    if (lastAddedPosition) {
        if (surveyCount !== lastUsedSurveyCount) {
            if (
                // position didn't move
                surveyPosition.coords.longitude ===
                    lastAddedPosition.coords.longitude &&
                surveyPosition.coords.latitude ===
                    lastAddedPosition.coords.latitude
            ) {
                lastUsedSurveyCount = surveyCount;
                return;
            } else {
                done();
                return;
            }
        } else {
            this.getPosition();
            retry();
            return;
        }
    } else {
        done();
        return;
    }
};

SurveyPolygonMode.StartRecording = function () {
    surveyInterval = setInterval(() => {
        this.getPosition();
        if (surveyPosition) {
            if (lastAddedPosition) {
                if (
                    // position moved
                    surveyPosition.coords.longitude !==
                        lastAddedPosition.coords.longitude ||
                    surveyPosition.coords.latitude !==
                        lastAddedPosition.coords.latitude
                ) {
                    this.addPointAndRender();
                }
            } else {
                this.addPointAndRender();
            }
        }
    }, 500);
};

SurveyPolygonMode.StopRecording = function () {
    clearInterval(surveyInterval);
    polygon.ctx.store.render();
    lastUsedSurveyCount = surveyCount;
};

SurveyPolygonMode.onSetup = function () {
    polygon = this.newFeature({
        type: Constants.geojsonTypes.FEATURE,
        properties: {},
        geometry: {
            type: Constants.geojsonTypes.POLYGON,
            coordinates: [[]],
        },
    });

    this.addFeature(polygon);

    this.clearSelectedFeatures();
    doubleClickZoom.disable(this);
    this.updateUIClasses({ mouse: Constants.cursors.ADD });
    this.activateUIButton(Constants.types.POLYGON);
    this.setActionableState({
        trash: true,
    });

    return {
        polygon,
        currentVertexPosition,
    };
};

SurveyPolygonMode.onStop = function (state) {
    this.updateUIClasses({ mouse: Constants.cursors.NONE });
    doubleClickZoom.enable(this);
    this.activateUIButton();

    // check to see if we've deleted this feature
    if (this.getFeature(state.polygon.id) === undefined) return;

    surveyPosition = null;
    lastAddedPosition = null;
    usablePosition = null;
    surveyCount = 0;
    lastUsedSurveyCount = 0;
    recentPositions = [];

    clearInterval(surveyInterval);

    if (state.polygon.isValid()) {
        clearInterval(surveyInterval);
        this.map.fire(Constants.events.CREATE, {
            features: [state.polygon.toGeoJSON()],
        });
    } else {
        this.deleteFeature([state.polygon.id], { silent: true });
        this.changeMode(Constants.modes.SIMPLE_SELECT, {}, { silent: true });
    }
};

SurveyPolygonMode.toDisplayFeatures = function (state, geojson, display) {
    const isActivePolygon = geojson.properties.id === state.polygon.id;
    geojson.properties.active = isActivePolygon
        ? Constants.activeStates.ACTIVE
        : Constants.activeStates.INACTIVE;
    if (!isActivePolygon) return display(geojson);

    if (geojson.geometry.coordinates.length === 0) return;

    const coordinateCount = geojson.geometry.coordinates[0].length;

    if (coordinateCount < 2) {
        return;
    }
    geojson.properties.meta = Constants.meta.FEATURE;
    display(
        createVertex(
            state.polygon.id,
            geojson.geometry.coordinates[0][0],
            '0.0',
            false,
        ),
    );
    if (coordinateCount > 2) {
        // Add a start position marker to the map, clicking on this will finish the feature
        // This should only be shown when we're in a valid spot
        const endPos = geojson.geometry.coordinates[0].length - 3;
        display(
            createVertex(
                state.polygon.id,
                geojson.geometry.coordinates[0][endPos],
                `0.${endPos}`,
                false,
            ),
        );
    }
    if (coordinateCount <= 4) {
        // If we've only drawn two positions (plus the closer),
        // make a LineString instead of a Polygon
        const lineCoordinates = [
            [
                geojson.geometry.coordinates[0][0][0],
                geojson.geometry.coordinates[0][0][1],
            ],
            [
                geojson.geometry.coordinates[0][1][0],
                geojson.geometry.coordinates[0][1][1],
            ],
        ];
        // create an initial vertex so that we can track the first point on mobile devices
        display({
            type: Constants.geojsonTypes.FEATURE,
            properties: geojson.properties,
            geometry: {
                coordinates: lineCoordinates,
                type: Constants.geojsonTypes.LINE_STRING,
            },
        });
        if (coordinateCount === 3) {
            return;
        }
    }
    // render the Polygon
    return display(geojson);
};

SurveyPolygonMode.onTrash = function (state) {
    this.deleteFeature([state.polygon.id], { silent: true });
    this.changeMode(Constants.modes.SIMPLE_SELECT);
};

export default SurveyPolygonMode;
